import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import {
  FloatingNode,
  FloatingPortal,
  autoUpdate,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useInteractions,
} from '@floating-ui/react';
import { useCopyToClipboard, useMeasure, useMediaQuery } from '@uidotdev/usehooks';
import { useLiveQuery } from 'dexie-react-hooks';
import { X } from 'lucide-react';
import { StickToBottom, useStickToBottomContext } from 'use-stick-to-bottom';

import { canStopAgentChat, resolveAgentChatStopTarget, stopAgentChat } from '../lib/agentChatStop';
import {
  beginAgentChatSubmission,
  isWidgetPromptLocked,
  latestAgentChatSubmission,
  settleAgentChatSubmission,
} from '../lib/agentChatSubmission';
import { buildAgentChatShareUrl, isQueuedRequestMessage } from '../lib/agent_chats';
import { apiDelete, apiGet, apiPost } from '../lib/api_v2';
import { BREAKPOINT_SM } from '../lib/breakpoints';
import db, { DEXIE_IS_LOADING } from '../lib/db';
import { buildMessageEntitySnapshots } from '../lib/entitySnapshots';
import { logError } from '../lib/logger';
import { MediaAttachmentOwnerTypes, removeMediaAttachmentsForSessionKey, updateMediaAttachment } from '../lib/media_attachments_store';
import { getMetaContent } from '../lib/meta';
import { sortULIDs } from '../lib/stringUtils';
import { cn } from '../lib/utils';
import {
  applyWidgetGenerationState,
  beginPendingWidgetGenerationTurn,
  storeAgentChatWithGenerationTurn,
} from '../lib/widgetGenerationTurns';

import AgentChatChevronIcon from '../../assets/images/agent-chat-chevron.svg';
import StopProcessingIcon from '../../assets/images/agent-chat-stop-processing.svg';
import NewAgentChatIcon from '../../assets/images/new-agent-chat.svg';
import ShareIcon from '../../assets/images/share.svg';

import cable from '../cable';
import { EntitySnapshotContext } from '../contexts/EntitySnapshotContext';
import { useRichdownComposerFocusRouter } from '../contexts/RichdownComposerFocusRouter';
import { useRouteWorkspace } from '../contexts/RouteWorkspaceContext';
import useComposerDropzone from '../hooks/useComposerDropzone';
import { AgentChatDropdown } from './AgentChatDropdown';
import RichdownComposer, { RichdownButton } from './RichdownComposer';
import { StreamdownWithEntities } from './StreamdownWithEntities';
import CenteredContentView from './layouts/CenteredContentView';
import { Attachment } from './messages/Attachment';
import DropOverlay from './messages/DropOverlay';
import RichdownContent from './messages/RichdownContent';
import { PopoverZIndexContext } from './shared/EntityPopover';
import { Flexicon } from './shared/Flexicon';
import { Tooltip } from './shared/Tooltip';

const EMPTY_ATTACHMENTS = Object.freeze([]);

const formatDuration = (seconds) => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return `${minutes}m ${remainingSeconds}s`;
};

export const AgentChatHeader = ({ agentChat, onAgentChatChange, onCreateAgentChat }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const nodeId = useFloatingNodeId();

  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const [, copyToClipboard] = useCopyToClipboard();
  const copiedTimeoutRef = useRef(null);

  const { refs, floatingStyles, context } = useFloating({
    nodeId,
    open: dropdownOpen,
    onOpenChange: setDropdownOpen,
    placement: 'bottom-start',
    middleware: [offset(10), shift({ padding: 5 })],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context, {
    outsidePress: () => true,
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

  const handleAgentChatSelect = useCallback(
    (selectedAgentChatId) => {
      setDropdownOpen(false);
      onAgentChatChange(selectedAgentChatId);
    },
    [onAgentChatChange],
  );

  const handleDropdownClose = useCallback(() => {
    setDropdownOpen(false);
  }, []);

  const handleNewChatClick = useCallback(() => {
    setDropdownOpen(false);
    onCreateAgentChat();
  }, [onCreateAgentChat]);

  const handleShareClick = useCallback(() => {
    copyToClipboard(buildAgentChatShareUrl(agentChat));
    if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
    setCopiedToClipboard(true);
    copiedTimeoutRef.current = setTimeout(() => setCopiedToClipboard(false), 250);
  }, [agentChat, copyToClipboard]);

  // close dropdown when agent chat changes
  useEffect(() => {
    setDropdownOpen(false);
  }, [agentChat?.id]);

  // cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current);
      }
    };
  }, []);

  return (
    <header className="flex size-14 w-full items-center justify-between p-3" data-app-region="drag">
      <button
        ref={refs.setReference}
        className="flex cursor-pointer items-center gap-1.5 truncate outline-none focus:outline-none"
        {...getReferenceProps()}
        data-app-region="none"
      >
        {agentChat?.status === 'running' && <div className="size-2 flex-shrink-0 rounded-full bg-green-500"></div>}
        {agentChat?.status === 'failed' && <div className="size-2 flex-shrink-0 rounded-full bg-red-500"></div>}
        <span className="truncate text-cursed text-nowrap text-black dark:text-gray-200">{agentChat?.title || 'Untitled'}</span>
        <AgentChatChevronIcon className="flex-shrink-0 text-black dark:text-gray-200" />
      </button>

      <div className="relative flex items-center" data-app-region="none">
        <Tooltip label="New chat">
          <button
            type="button"
            className="flex size-9 flex-shrink-0 cursor-pointer items-center justify-center rounded-full text-black hover:bg-gray-200 dark:text-gray-200 dark:hover:bg-neutral-800"
            onClick={handleNewChatClick}
            aria-label="New chat"
          >
            <NewAgentChatIcon className="text-black dark:text-gray-200" />
          </button>
        </Tooltip>

        {agentChat && (
          <Tooltip label="Copy share link">
            <button
              type="button"
              className="flex size-9 flex-shrink-0 cursor-pointer items-center justify-center rounded-full text-black hover:bg-gray-200 dark:text-gray-200 dark:hover:bg-neutral-800"
              onClick={handleShareClick}
              aria-label="Copy share link"
            >
              <ShareIcon className={`size-4 text-black transition-transform dark:text-gray-200 ${copiedToClipboard ? 'scale-110' : ''}`} />
            </button>
          </Tooltip>
        )}
      </div>

      {dropdownOpen && (
        <FloatingNode id={nodeId}>
          <FloatingPortal>
            <div
              ref={refs.setFloating}
              style={{ ...floatingStyles, zIndex: 'var(--stack-agent-chat-dropdown)' }}
              {...getFloatingProps()}
              data-app-region="none"
            >
              <AgentChatDropdown
                currentAgentChatId={agentChat?.id}
                onAgentChatSelect={handleAgentChatSelect}
                onClose={handleDropdownClose}
              />
            </div>
          </FloatingPortal>
        </FloatingNode>
      )}
    </header>
  );
};

const AgentChatFooter = ({
  agentChat,
  centered,
  compact = false,
  composerPlaceholder,
  composerAutoSubmit,
  composerAutoSubmitAttachments = EMPTY_ATTACHMENTS,
  submitAgentChatMessage,
  dropzoneHelpers,
  onDropzoneStateChange,
  onSubmitMessage,
  onUploadCountChange,
  maxMediaAttachments,
  maxRichdownContentSize,
  composerDisabled,
  stopTarget,
  onSubmittedRequestChange,
}) => {
  const { workspaceId, mainChannelId } = useRouteWorkspace();
  const { scrollToBottom } = useStickToBottomContext();
  const { focusAgentChatComposer } = useRichdownComposerFocusRouter();
  const isBreakpointSm = useMediaQuery(BREAKPOINT_SM);

  // Skip autofocus on mobile - it can trigger unwanted scroll behavior
  const handleComposerReady = useCallback(() => {
    if (isBreakpointSm) return;
    focusAgentChatComposer();
  }, [focusAgentChatComposer, isBreakpointSm]);
  const lastUploadCountRef = useRef(0);
  const widgetSubmissionPendingRef = useRef(false);
  const [widgetSubmissionPending, setWidgetSubmissionPending] = useState(false);
  const widgetPromptLocked = isWidgetPromptLocked(agentChat, widgetSubmissionPending);

  useEffect(() => {
    widgetSubmissionPendingRef.current = false;
    setWidgetSubmissionPending(false);
  }, [agentChat?.id]);

  useEffect(() => {
    if (!widgetSubmissionPending || agentChat?.status !== 'running') return;

    widgetSubmissionPendingRef.current = false;
    setWidgetSubmissionPending(false);
  }, [agentChat?.status, widgetSubmissionPending]);

  // scroll to bottom when a new attachment row appears
  const handleUploadCountChangeWrapped = useCallback(
    (count) => {
      if (lastUploadCountRef.current === 0 && count > 0) scrollToBottom();
      lastUploadCountRef.current = count;
      onUploadCountChange?.(count);
    },
    [onUploadCountChange, scrollToBottom],
  );

  const agentChatId = agentChat?.id;
  const [autoSubmitRecoveryPrompt, setAutoSubmitRecoveryPrompt] = useState('');
  const [isStopping, setIsStopping] = useState(false);

  useEffect(() => {
    setIsStopping(false);
  }, [agentChatId]);

  // Keep overlapping submissions independently until each settles. A failed later send
  // must reveal an earlier successful send whose request has not reached Cable yet.
  const submissionSequenceRef = useRef(0);
  const submittedRequestsRef = useRef(new Map());
  const reportSubmittedRequests = useCallback(() => {
    onSubmittedRequestChange?.(latestAgentChatSubmission(submittedRequestsRef.current));
  }, [onSubmittedRequestChange]);

  useEffect(() => {
    submittedRequestsRef.current = new Map();
  }, [agentChatId]);

  const handleSubmitMessage = useCallback(
    async (message, attachments = []) => {
      if (!agentChatId) return false;
      if (isWidgetPromptLocked(agentChat, widgetSubmissionPendingRef.current)) return false;

      // ensure content is not empty
      const user_text = message?.trim();
      if (!user_text) return false;

      if (agentChat?.kind === 'widget') {
        widgetSubmissionPendingRef.current = true;
        setWidgetSubmissionPending(true);
      }

      onSubmitMessage?.();
      scrollToBottom();
      // The turn this submission opens has no id yet, and the turn it replaces is the
      // wrong thing to stop: the server would treat that as an already-settled no-op
      // while the new generation ran on. Record what Stop must not aim at until the
      // new request surfaces.
      const submissionSequence = (submissionSequenceRef.current += 1);
      // Whichever turn is current stays current if this submission never opens one. A
      // widget submission sent mid-turn queues rather than replacing the running turn,
      // so it must not hide Stop for the turn that keeps running.
      const replacedRequestMessageId =
        agentChat?.kind === 'widget' && agentChat?.status === 'running' ? null : stopTarget?.requestMessageId || null;
      submittedRequestsRef.current = beginAgentChatSubmission(submittedRequestsRef.current, {
        agentChatId,
        sequence: submissionSequence,
        pending: true,
        replacedRequestMessageId,
      });
      reportSubmittedRequests();

      try {
        let data;

        if (submitAgentChatMessage) {
          data = await submitAgentChatMessage({
            agentChatId,
            attachments,
            message: user_text,
          });
        } else {
          const response = await apiPost(`/agent_chats/${agentChatId}/messages`, {
            agent_chat_message: {
              user_text,
              attachments: attachments.map((attachment) => attachment.attachment_id),
            },
          });

          data = await response.json();
        }

        if (data?.ok) {
          const submittedMessage = data.data?.agent_chat_message;
          // The server's word, not a prediction from cached chat state: only widget
          // chats queue, and a queued message opened no turn for Stop to target.
          const submissionQueued = submittedMessage?.status === 'queued';

          // Cable delivers the request message a beat later; the response already names
          // it, so Stop can target the new turn as soon as it exists.
          submittedRequestsRef.current = settleAgentChatSubmission(
            submittedRequestsRef.current,
            submissionSequence,
            submissionQueued
              ? null
              : {
                  agentChatId,
                  sequence: submissionSequence,
                  requestMessageId: submittedMessage?.id || null,
                },
          );
          reportSubmittedRequests();

          if (agentChat?.kind === 'widget') {
            const submittedAgentChat = data.data?.agent_chat;
            try {
              if (submittedAgentChat) await storeAgentChatWithGenerationTurn(submittedAgentChat);
              else await db.agent_chats.update([workspaceId, agentChatId], { status: 'running' });
            } catch (error) {
              logError('agent-chat', 'Failed to cache active widget turn', error);
            }

            // Queued submissions began no turn; the pending flag clears via the
            // status-running effect once the stored chat lands.
            if (!submissionQueued) {
              beginPendingWidgetGenerationTurn({
                workspaceId,
                agentChatId,
                requestMessageId: submittedMessage?.id,
                startedAt: Math.floor(Date.now() / 1000),
              });
            }
          }
          scrollToBottom();
          return true;
        } else {
          widgetSubmissionPendingRef.current = false;
          setWidgetSubmissionPending(false);
          submittedRequestsRef.current = settleAgentChatSubmission(submittedRequestsRef.current, submissionSequence, null);
          reportSubmittedRequests();
          logError('agent-chat', `Failed to send message: ${data?.errors}`);
          return false;
        }
      } catch (error) {
        widgetSubmissionPendingRef.current = false;
        setWidgetSubmissionPending(false);
        submittedRequestsRef.current = settleAgentChatSubmission(submittedRequestsRef.current, submissionSequence, null);
        reportSubmittedRequests();
        logError('agent-chat', 'Failed to send message', error);
        return false;
      }
    },
    [agentChat, agentChatId, onSubmitMessage, reportSubmittedRequests, scrollToBottom, stopTarget, submitAgentChatMessage, workspaceId],
  );

  // Keyed on chat + text rather than a bare boolean: re-renders must not resend, but a
  // genuinely new chat carrying a new prompt must still go.
  const autoSubmitCompletedKeyRef = useRef(null);
  const autoSubmitInFlightKeyRef = useRef(null);
  const autoSubmitRecoveryKeyRef = useRef(null);
  useEffect(
    function sendAutoSubmitPrompt() {
      const prompt = composerAutoSubmit?.trim();
      // composerDisabled is the chat's own not-ready-yet signal; submitting before then
      // races the message load and the turn lands in an empty transcript.
      if (!prompt || !agentChatId || composerDisabled) return;

      const submissionKey = `${agentChatId}:${prompt}`;
      if (
        autoSubmitCompletedKeyRef.current === submissionKey ||
        autoSubmitInFlightKeyRef.current === submissionKey ||
        autoSubmitRecoveryKeyRef.current === submissionKey
      ) {
        return;
      }

      if (autoSubmitRecoveryKeyRef.current) {
        autoSubmitRecoveryKeyRef.current = null;
        setAutoSubmitRecoveryPrompt('');
      }

      autoSubmitInFlightKeyRef.current = submissionKey;

      async function submitPrompt() {
        const submitted = await handleSubmitMessage(prompt, composerAutoSubmitAttachments);
        if (autoSubmitInFlightKeyRef.current !== submissionKey) return;

        autoSubmitInFlightKeyRef.current = null;
        if (submitted) {
          autoSubmitCompletedKeyRef.current = submissionKey;
          // The seed already posted with these attachment_ids — the empty-state
          // screen kept its upload rows alive on the chance this send failed, so now
          // that it hasn't, it's safe to drop them.
          const seed = composerAutoSubmitAttachments?.[0];
          if (seed) {
            removeMediaAttachmentsForSessionKey(seed.session_key, {
              ownerType: seed.owner_type,
              ownerId: seed.owner_id,
            }).catch((error) => logError('agent-chat', 'Failed to clean up seeded attachments', error));
          }
          return;
        }

        // Keep a failed seed in the real composer so the viewer can retry or edit it
        // without reconstructing the prompt from the empty-state screen. Attachments
        // ride the same way: reparent their upload rows into this composer's own
        // channel/session so they show up already attached, ready to resend.
        autoSubmitRecoveryKeyRef.current = submissionKey;
        setAutoSubmitRecoveryPrompt(prompt);
        if (composerAutoSubmitAttachments?.length) {
          const recoveryChannelId = `agent-chat-${agentChatId}`;
          const recoverySessionKey = `richdown-composer:${recoveryChannelId}`;
          Promise.all(
            composerAutoSubmitAttachments.map((attachment) =>
              updateMediaAttachment(attachment.id, {
                channel_id: recoveryChannelId,
                session_key: recoverySessionKey,
                owner_type: MediaAttachmentOwnerTypes.composer,
                owner_id: recoveryChannelId,
              }),
            ),
          ).catch((error) => logError('agent-chat', 'Failed to recover seeded attachments after a failed send', error));
        }
      }

      submitPrompt();
    },
    [agentChatId, composerAutoSubmit, composerAutoSubmitAttachments, composerDisabled, handleSubmitMessage],
  );

  const handleClearAttachments = useCallback(
    async (attachmentIds) => {
      const ids = attachmentIds.filter(Boolean);
      if (!ids.length || !workspaceId) return;

      const fromAttachmentId = await db.media_attachments.where('attachment_id').anyOf(ids).toArray();
      const fromRowId = await db.media_attachments.where('id').anyOf(ids).toArray();
      const byPk = new Map();
      [...fromAttachmentId, ...fromRowId].forEach((row) => {
        if (row?.workspace_id === workspaceId) byPk.set(row.id, row);
      });
      const toDelete = [...byPk.keys()];
      if (toDelete.length) await db.media_attachments.bulkDelete(toDelete);
    },
    [workspaceId],
  );

  const handleStop = useCallback(async () => {
    if (isStopping || !canStopAgentChat(agentChat, stopTarget)) return;

    setIsStopping(true);
    try {
      await stopAgentChat({ agentChat, stopTarget, workspaceId });
    } catch (error) {
      logError('agent-chat', 'Failed to stop agent chat', error);
    } finally {
      setIsStopping(false);
    }
  }, [agentChat, isStopping, stopTarget, workspaceId]);

  const showsStopButton = canStopAgentChat(agentChat, stopTarget);
  // Only the stop state needs its own button. Leaving `submitButton` undefined the rest
  // of the time hands the send state back to the composer's own default, which is the
  // one a channel renders — same blue fill, same icon, same hide-when-empty.
  const renderStopButton = useCallback(
    ({ editor }) => (
      <RichdownButton
        editor={editor}
        className="flex items-center justify-center bg-neutral-950 dark:bg-indigo-500"
        onClick={handleStop}
        disabled={isStopping}
        ariaLabel="Stop processing"
      >
        <StopProcessingIcon />
      </RichdownButton>
    ),
    [handleStop, isStopping],
  );

  return (
    <footer className={cn('z-(--stack-agent-chat-richdown-composer)', centered ? 'relative w-full' : 'absolute right-0 bottom-0 left-0')}>
      {!centered && <div className="absolute right-0 bottom-0 left-0 h-9" />}
      {/* pt-0 stays last: cn merges Tailwind conflicts by order, so a later `p-*` would
          swallow it and put padding back above the composer. */}
      <div className={cn('non-interactive-entities relative w-full', compact ? 'p-5' : 'p-3', 'pt-0')}>
        <RichdownComposer
          key={agentChat?.id}
          editorId={`agent-chat-${agentChat?.id}`}
          workspaceId={workspaceId}
          mainChannelId={mainChannelId}
          placeholder={composerPlaceholder}
          initialContent={autoSubmitRecoveryPrompt}
          // The studio renders its bubbles smaller than a channel does, and the composer
          // is supposed to read as the same text you are about to send.
          className={compact ? 'text-[13px]/[18px]' : ''}
          dense={compact}
          variant="agent_chat"
          onReady={handleComposerReady}
          hideToolbar={true}
          onSubmit={handleSubmitMessage}
          onClearAttachments={handleClearAttachments}
          dropzoneHelpers={dropzoneHelpers}
          onDropzoneStateChange={onDropzoneStateChange}
          onUploadCountChange={handleUploadCountChangeWrapped}
          maxFiles={maxMediaAttachments}
          maxRichdownContentSize={maxRichdownContentSize}
          submitButton={showsStopButton ? renderStopButton : undefined}
          containerClassName="relative flex flex-col richdown-composer-wrapper rounded-3xl border border-gray-300/30 shadow-none dark:border-neutral-950"
          disabled={composerDisabled || widgetPromptLocked}
        />
      </div>
    </footer>
  );
};

const LoadingSpinner = () => {
  return (
    <div className="flex size-full animate-fade-in-delayed items-center justify-center gap-2 opacity-0">
      <div className="size-2 animate-bounce rounded-full bg-neutral-300 dark:bg-neutral-700"></div>
      <div className="size-2 animate-bounce rounded-full bg-neutral-300 [animation-delay:0.2s] dark:bg-neutral-700"></div>
      <div className="size-2 animate-bounce rounded-full bg-neutral-300 [animation-delay:0.4s] dark:bg-neutral-700"></div>
    </div>
  );
};

const UnknownMessage = ({ data }) => {
  return (
    <div className="max-w-full rounded-lg bg-neutral-100 p-2 font-mono text-3xs text-neutral-500">{JSON.stringify(data, null, 2)}</div>
  );
};

const RequestMessage = ({ data, status, showAuthor = false }) => {
  const entitySnapshots = useContext(EntitySnapshotContext);
  const messageEntitySnapshots = useMemo(() => buildMessageEntitySnapshots(data.text, entitySnapshots), [data.text, entitySnapshots]);

  return (
    <div className="flex flex-col items-end gap-1">
      {showAuthor && data.author?.full_name ? (
        <div className="flex items-center gap-1 px-1 font-sans text-[11px]/[16px] font-medium text-gray-400 dark:text-neutral-500">
          <Flexicon resource={data.author} size="size-4" />
          <span>{data.author.full_name}</span>
        </div>
      ) : null}
      {data.attachments.length > 0 &&
        (data.attachments.length === 1 ? (
          <div className="block">
            <Attachment key={data.attachments[0].id} attachment={data.attachments[0]} mode="single" />
          </div>
        ) : (
          <div className="relative ml-11 inline-flex max-w-full">
            <div className="flex flex-wrap justify-end gap-2">
              {data.attachments.map((attachment) => (
                <Attachment key={attachment.id} attachment={attachment} mode="group" />
              ))}
            </div>
          </div>
        ))}

      {!data.contains_only_attachments && (
        <div className={`richdown-bubble--base richdown-bubble--agent-chat-request ${status === 'refused' ? 'opacity-50' : ''}`}>
          <RichdownContent
            html={data.text}
            richdownVariant="richdown-content--agent-chat-request"
            entitySnapshots={messageEntitySnapshots}
            containsOnlyEntities={data.contains_only_entities}
            containsOnlyEmojiEntities={data.contains_only_emoji_entities}
          />
        </div>
      )}
    </div>
  );
};

const ResponseMessage = ({ data }) => {
  return (
    <div className="flex flex-col items-start">
      <div className="max-w-full overflow-x-hidden text-black dark:text-gray-200">
        <StreamdownWithEntities variant="agent-response">{data.text}</StreamdownWithEntities>
      </div>
    </div>
  );
};

const ThinkingMessage = ({ data }) => {
  return (
    <div className="flex flex-col gap-1 px-1">
      <div className="font-mono text-2xs font-bold text-gray-600 dark:text-neutral-400">Thinking</div>
      <StreamdownWithEntities variant="agent-thinking">{data.thinking}</StreamdownWithEntities>
    </div>
  );
};

const ToolUseMessage = ({ data }) => {
  let title;

  switch (data.name) {
    case 'internal_get_workspace_channel_messages':
      title = 'Get channel messages';
      break;
    case 'internal_search_workspace_channel_messages':
      title = 'Search messages';
      break;
    case 'internal_get_workspace_channels':
      title = 'Get channels';
      break;
    case 'internal_get_workspace_profiles':
      title = 'Get profiles';
      break;
    case 'internal_create_workspace_channel':
      title = 'Create channel';
      break;
    case 'internal_update_workspace_channel':
      title = 'Update channel';
      break;
    case 'internal_download_workspace_media_attachment':
      title = `Download ${data.output?.metadata?.file_name || 'media attachment'}`;
      break;
    case 'external_get_world_time':
      title = 'Get world time';
      break;
    case 'external_get_realtime_weather':
      title = 'Get weather';
      break;
    case 'external_search_news_articles':
      title = 'Search news';
      break;
    case 'external_execute_linear_graphql_query':
      title = (data.input?.query || '').includes('mutation') ? 'Update Linear' : 'Search Linear';
      break;
    case 'external_execute_github_graphql_query':
      title = (data.input?.query || '').includes('mutation') ? 'Update GitHub' : 'Search GitHub';
      break;
    default:
      title = `Use ${(data.name || '').replace('internal_', '').replace('external_', '').toLowerCase()} tool`;
      break;
  }

  const json = JSON.stringify(data.input, null, 2);

  return (
    <div className="flex flex-col gap-1 px-1">
      <div className="font-mono text-2xs font-bold text-gray-600 dark:text-neutral-400">{title}</div>
      {json !== '{}' && <div className="font-mono text-3xs whitespace-pre-wrap text-gray-400 dark:text-neutral-400">{json}</div>}
    </div>
  );
};

const ToolResultMessage = ({ data }) => {
  if (!data.is_error && data.content?.review_required && data.content?.widget_generation_run) {
    return null;
  }

  const json = JSON.stringify(data.content, null, 2);

  return (
    <div className="flex flex-col gap-1 px-1">
      {!data.is_error && <div className="font-mono text-2xs font-bold text-gray-600 dark:text-neutral-400">Output</div>}
      {data.is_error && <div className="font-mono text-2xs font-bold text-red-500 dark:text-red-400">Error</div>}
      {json !== '{}' && <div className="font-mono text-3xs whitespace-pre-wrap text-gray-400 dark:text-neutral-400">{json}</div>}
    </div>
  );
};

const MessageContent = ({ message, showAuthor = false }) => {
  let content = null;

  switch (message.kind) {
    case 'request':
      content = <RequestMessage data={message.request_data} status={message.status} showAuthor={showAuthor} />;
      break;
    case 'response':
      content = <ResponseMessage data={message.response_data} />;
      break;
    case 'thinking':
      content = <ThinkingMessage data={message.thinking_data} />;
      break;
    case 'tool_use':
      content = <ToolUseMessage data={message.tool_use_data} />;
      break;
    case 'tool_result':
      content = <ToolResultMessage data={message.tool_result_data} />;
      break;
  }

  return (
    <div data-debug-id={message.id} data-debug-sort-order={message.sort_order} className="flex flex-col">
      {content || <UnknownMessage data={message} />}
    </div>
  );
};

const MessagesChunk = ({ messages = [] }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [measureRef, { height }] = useMeasure();

  const overflowHeight = 80; // h-20 = 80px
  const needsOverflow = height > overflowHeight + 10;

  const handleToggleCollapse = () => {
    setIsCollapsed((prev) => !prev);
  };

  const shouldClip = isCollapsed && needsOverflow;

  // calculate duration span across entire chunk (from first message start to last message end)
  const chunkDuration = useMemo(() => {
    if (messages.length === 0) return null;

    // find first message with start time
    const firstMessage = messages.find((msg) => msg.processing_start_time);
    if (!firstMessage) return null;

    // find last message with end time
    const lastMessage = [...messages].reverse().find((msg) => msg.processing_end_time);
    if (!lastMessage) return null;

    const startTime = firstMessage.processing_start_time;
    const endTime = lastMessage.processing_end_time;
    if (!startTime || !endTime) return null;

    return endTime - startTime;
  }, [messages]);

  return (
    <div className="flex flex-col items-start gap-2">
      <button
        className={`flex items-center gap-1 ${needsOverflow ? 'cursor-pointer' : 'cursor-default'}`}
        onClick={needsOverflow ? handleToggleCollapse : undefined}
      >
        {chunkDuration === null && <div className="text-2xs text-gray-500 dark:text-neutral-500">Planning</div>}
        {chunkDuration !== null && (
          <div className="text-2xs text-gray-500 dark:text-neutral-500">Planned for {formatDuration(chunkDuration)}</div>
        )}
        {needsOverflow &&
          (isCollapsed ? (
            <AgentChatChevronIcon className="-rotate-90 text-gray-400 dark:text-neutral-500" />
          ) : (
            <AgentChatChevronIcon className="text-gray-400 dark:text-neutral-500" />
          ))}
      </button>
      <div
        onClick={shouldClip ? handleToggleCollapse : undefined}
        className={`relative w-full border-l border-gray-300 pl-1 dark:border-neutral-600 ${shouldClip ? 'mask-gradient-fade-top cursor-pointer overflow-hidden' : ''}`}
        style={shouldClip ? { height: `${overflowHeight}px` } : {}}
      >
        <div ref={measureRef} className={`flex flex-col gap-2 pl-1 ${shouldClip ? 'absolute right-0 bottom-0 left-0' : ''}`}>
          {messages.map((message) => (
            <MessageContent key={message.id} message={message} />
          ))}
        </div>
      </div>
    </div>
  );
};

const ContentOverlay = ({ isLoadingMessages, isContentReady, onContentReady }) => {
  const { scrollToBottom } = useStickToBottomContext();
  const contentRef = useRef(null);
  const hasTriggeredReadyRef = useRef(false);
  const [shouldTransition, setShouldTransition] = useState(false);

  // handle scroll snapping and fade-out after messages load
  useEffect(() => {
    // reset ready state when loading starts
    if (isLoadingMessages) {
      hasTriggeredReadyRef.current = false;
      setShouldTransition(false);
      return;
    }

    // only trigger once when loading completes
    if (hasTriggeredReadyRef.current) return;

    // wait for DOM to be ready and content to exist
    const checkAndSnap = async () => {
      if (!contentRef.current) {
        requestAnimationFrame(checkAndSnap);
        return;
      }

      // wait for next frame to ensure DOM is ready
      await new Promise((resolve) => requestAnimationFrame(resolve));

      // snap to bottom immediately without animation and wait for it to complete
      await scrollToBottom();

      // enable transition, then wait one more frame before fading out overlay
      setShouldTransition(true);
      requestAnimationFrame(() => {
        hasTriggeredReadyRef.current = true;
        onContentReady();
      });
    };

    checkAndSnap();
  }, [isLoadingMessages, scrollToBottom, onContentReady]);

  // show overlay when loading or content not ready
  const showOverlay = isLoadingMessages || !isContentReady;

  return (
    <div
      ref={contentRef}
      className={cn(
        'absolute top-0 right-0 bottom-0 left-0 z-(--stack-agent-chat-content-overlay) dark:outline-gray-200/5',
        shouldTransition && 'transition-opacity duration-200',
        showOverlay ? 'opacity-100' : 'pointer-events-none opacity-0',
      )}
    >
      {isLoadingMessages && (
        <div className="flex size-full items-center justify-center">
          <LoadingSpinner />
        </div>
      )}
    </div>
  );
};

// Main agent chat surface for a specific chat id. Route/bootstrap logic lives in
// AgentChatPageView so this component can focus on loading, streaming, and rendering chat content.
const AgentChatView = ({
  agentChatId,
  composerPlaceholder = 'Ask anything',
  // The studio runs tighter than the standalone agent chat: it shares its column with a
  // preview, so the same gutters read as wasted width there.
  compact = false,
  // Sent for the viewer the moment the chat is ready. The studio's empty state already
  // took the prompt, so landing in the builder should land mid-turn, not on a filled-in
  // box waiting for a second Enter.
  composerAutoSubmit = '',
  composerAutoSubmitAttachments = EMPTY_ATTACHMENTS,
  onAgentChatChange,
  onCreateAgentChat,
  onSubmitNewChatMessage,
  pinComposerToBottom = false,
  showHeader = true,
  submitAgentChatMessage,
  onStopTargetChange,
}) => {
  const { workspaceId, profile: currentProfile } = useRouteWorkspace();
  const agentChat = useLiveQuery(
    async () => (workspaceId && agentChatId ? db.agent_chats.get([workspaceId, agentChatId]) : null),
    [workspaceId, agentChatId],
    DEXIE_IS_LOADING,
  );

  const [messages, setMessages] = useState([]);
  const [messagesAgentChatId, setMessagesAgentChatId] = useState(null);
  // The request this view just submitted: `{ pending: true }` while the post is in
  // flight, then the id the server assigned it. Held only until the transcript catches up.
  const [submittedRequest, setSubmittedRequest] = useState(null);
  const [hasStartedConversation, setHasStartedConversation] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isContentReady, setIsContentReady] = useState(false);

  const AGENT_CHAT_MESSAGE_MAX_MEDIA_ATTACHMENTS = useMemo(() => parseInt(getMetaContent('agent_chat_message:max_media_attachments')), []);
  const AGENT_CHAT_MESSAGE_MAX_RICHDOWN_CONTENT_SIZE = useMemo(
    () => parseInt(getMetaContent('agent_chat_message:max_richdown_content_size')),
    [],
  );

  const { dropzoneHelpers, getRootProps, handleDropzoneStateChange, handleUploadCountChange, showDropzoneOverlay } = useComposerDropzone({
    isInteractive: true,
  });

  // helper to merge messages, updating existing ones and adding new ones
  const mergeMessages = useCallback((newMessages) => {
    setMessages((prev) => {
      const messageMap = new Map(prev.map((msg) => [msg.id, msg]));
      newMessages.forEach((msg) => messageMap.set(msg.id, msg));
      return Array.from(messageMap.values()).sort((a, b) => sortULIDs(a.sort_order, b.sort_order));
    });
  }, []);

  // The destroy broadcast is what removes the row — the same path every client takes.
  const handleRemoveQueuedMessage = useCallback(
    async (event) => {
      const messageId = event.currentTarget.dataset.messageId;

      try {
        const response = await apiDelete(`/agent_chats/${agentChatId}/messages/${messageId}`);
        const data = await response.json();
        if (!response.ok || !data.ok) {
          logError('agent-chat', 'Failed to remove queued message', data?.errors);
        } else if (data.data?.agent_chat) {
          await storeAgentChatWithGenerationTurn(data.data.agent_chat);
        }
      } catch (error) {
        logError('agent-chat', 'Failed to remove queued message', error);
      }
    },
    [agentChatId],
  );

  // load initial messages when agentChatId changes
  useEffect(() => {
    setMessages([]);
    setMessagesAgentChatId(null);
    setSubmittedRequest(null);
    setIsLoadingMessages(true);
    setIsContentReady(false);
    if (!agentChatId) {
      setIsLoadingMessages(false);
      setIsContentReady(true);
      return;
    }

    const abortController = new AbortController();

    const fetchMessages = async () => {
      try {
        const response = await apiGet(`/agent_chats/${agentChatId}/messages`, { signal: abortController.signal });
        const data = await response.json();
        if (!data.ok || !data.data?.agent_chat_messages) {
          setIsLoadingMessages(false);
          setIsContentReady(true);
          return;
        }
        mergeMessages(data.data.agent_chat_messages);
        setMessagesAgentChatId(agentChatId);
        setIsLoadingMessages(false);
        setIsContentReady(true);
      } catch (error) {
        // ignore abort errors
        if (error.name === 'AbortError') return;
        logError('agent-chat', `Failed to fetch messages: ${error}`);
        setIsLoadingMessages(false);
        setIsContentReady(true);
      }
    };

    fetchMessages();

    // cancel fetch if agentChatId changes
    return () => {
      abortController.abort();
    };
  }, [agentChatId, mergeMessages]);

  useEffect(() => {
    setHasStartedConversation(false);
  }, [agentChatId]);

  useEffect(() => {
    if (messages.some((message) => message.kind === 'request')) {
      setHasStartedConversation(true);
    }
  }, [messages]);

  // subscribe to cable events
  useEffect(() => {
    if (!agentChat?.signed_streaming_key) return;

    const subscription = cable.subscribeTo('AgentChatChannel', {
      workspace_id: workspaceId,
      signed_agent_chat_streaming_key: agentChat.signed_streaming_key,
    });

    subscription._unbindOnMessage = subscription.on('message', (message) => {
      const { action, data } = message;
      if (action === 'widget_generation_run') {
        if (data?.generation_turn || data?.working_revision) {
          applyWidgetGenerationState(data, { workspaceId, agentChatId });
        }
        return;
      }
      if (!data?.agent_chat_message) return;

      const acm = data.agent_chat_message;

      switch (action) {
        case 'create':
        case 'update':
          mergeMessages([acm]);
          break;
        case 'destroy':
          setMessages((prev) => prev.filter((m) => m.id !== acm.id));
          break;
      }
    });

    return () => {
      if (subscription._unbindOnMessage) subscription._unbindOnMessage();
      subscription.disconnect();
    };
  }, [agentChat?.signed_streaming_key, agentChatId, mergeMessages, workspaceId]);

  // control gradient animation based on agent chat status
  useEffect(() => {
    const isRunning = agentChat?.status === 'running';
    window.dispatchEvent(
      new CustomEvent('output:internal:animate-agent-chat-gradient', {
        detail: { enabled: isRunning },
      }),
    );
  }, [agentChat?.status]);

  // chunk messages into chunks that reset on request or response; queued requests sit
  // outside the transcript in their own strip until promotion re-sorts them into it
  const { chunkedMessages, queuedMessages } = useMemo(() => {
    const chunks = [];
    const queued = [];
    let current = { standalone: false, messages: [] };

    for (const message of messages) {
      if (isQueuedRequestMessage(message)) {
        queued.push(message);
        continue;
      }

      // request and response messages always go standalone
      if (message.kind === 'request' || message.kind === 'response') {
        // add current chunk if it has messages
        if (current.messages.length > 0) chunks.push(current);

        // create a new chunk with just this request/response message
        chunks.push({ id: message.id, standalone: true, messages: [message] });

        // start a new non-standalone chunk for subsequent messages
        current = { standalone: false, messages: [] };
      } else {
        // all other message types group with the current chunk
        current.messages.push(message);

        // use the first message's id as the chunk id so its stable
        if (!current.id) current.id = message.id;
      }
    }

    // add the last chunk if it was populated
    if (current.messages.length > 0) chunks.push(current);

    // filter out any existing synthetic chunks (they'll be replaced by real planning messages)
    const filteredChunks = chunks.filter((chunk) => !chunk.id?.startsWith('synthetic-planning-'));

    // add synthetic planning chunk when agent is processing and last chunk is a request
    const isProcessing = agentChat?.status === 'running';
    const lastChunk = filteredChunks[filteredChunks.length - 1];
    const lastChunkIsRequest = lastChunk?.standalone && lastChunk?.messages[0]?.kind === 'request';
    const hasPlanningAfterRequest = current.messages.length > 0;

    if (isProcessing && lastChunkIsRequest && !hasPlanningAfterRequest) {
      // create synthetic planning chunk with empty messages array (just shows "Planning" header)
      filteredChunks.push({ id: `synthetic-planning-${lastChunk.id}`, standalone: false, messages: [] });
    }

    return { chunkedMessages: filteredChunks, queuedMessages: queued };
  }, [messages, agentChat?.status]);

  const handleContentReady = useCallback(() => {
    setIsContentReady(true);
  }, []);

  const handleSubmitMessage = useCallback(() => {
    setHasStartedConversation(true);
    onSubmitNewChatMessage?.(agentChatId);
  }, [agentChatId, onSubmitNewChatMessage]);

  const stopTarget = useMemo(
    () =>
      resolveAgentChatStopTarget({
        agentChatId,
        isTranscriptLoaded: isContentReady && !isLoadingMessages && messagesAgentChatId === agentChatId,
        messages,
        submittedRequest,
      }),
    [agentChatId, isContentReady, isLoadingMessages, messages, messagesAgentChatId, submittedRequest],
  );
  const manageableStopTarget = useMemo(() => {
    if (!stopTarget || agentChat?.kind !== 'widget') return stopTarget;

    const request = messages.find((message) => String(message.id) === String(stopTarget.requestMessageId));
    const authorProfileId = request?.request_data?.author?.id;
    if (!authorProfileId) return stopTarget;

    return String(authorProfileId) === String(currentProfile?.id) ? stopTarget : null;
  }, [agentChat?.kind, currentProfile?.id, messages, stopTarget]);

  useEffect(
    function reportStopTarget() {
      onStopTargetChange?.(manageableStopTarget);
      return () => onStopTargetChange?.(null);
    },
    [manageableStopTarget, onStopTargetChange],
  );

  // show loading placeholder while loading
  if (agentChat === DEXIE_IS_LOADING) return <LoadingSpinner />;

  // show placeholder if it doesn't exist
  if (!agentChat) {
    return (
      <div className="flex size-full flex-col overflow-auto">
        {showHeader ? <AgentChatHeader onAgentChatChange={onAgentChatChange} onCreateAgentChat={onCreateAgentChat} /> : null}
        <div className="flex size-full flex-col items-center justify-center gap-2">
          <div className="animate-fade-in-delayed text-lg font-medium text-gray-400 italic opacity-0 dark:text-neutral-400">
            Conversation not found
          </div>
        </div>
      </div>
    );
  }

  const dropzoneClassName = `relative flex h-full flex-1 flex-col dropzone-target ${showDropzoneOverlay ? 'dropzone-active' : ''}`;
  const rootProps = dropzoneHelpers ? getRootProps({ className: dropzoneClassName }) : { className: dropzoneClassName };

  const hasMessages = chunkedMessages.length > 0;
  const showCenteredFooter = !pinComposerToBottom && !hasMessages && !hasStartedConversation;
  const showLoadedContent = !isLoadingMessages;

  const mainColumn = (
    <PopoverZIndexContext.Provider value="z-(--stack-agent-chat-toolbar)">
      <StickToBottom.Content
        className={cn(
          'flex flex-col',
          // pt is its own step: the studio's transcript starts right under a header, and
          // the tight gutters that suit its narrow column read as cramped up there.
          compact ? 'p-1 pt-6' : 'p-4',
          showLoadedContent && hasMessages && 'space-y-4',
          showLoadedContent && showCenteredFooter && 'min-h-full',
        )}
      >
        <ContentOverlay isLoadingMessages={isLoadingMessages} isContentReady={isContentReady} onContentReady={handleContentReady} />
        {showLoadedContent && (
          <>
            {chunkedMessages.map((chunk) => (
              <React.Fragment key={chunk.id}>
                {chunk.standalone ? (
                  <MessageContent message={chunk.messages[0]} showAuthor={agentChat.kind === 'widget'} />
                ) : (
                  <MessagesChunk messages={chunk.messages} />
                )}
              </React.Fragment>
            ))}
            {queuedMessages.length > 0 && (
              <div className="flex flex-col gap-2">
                {queuedMessages.map((message) => (
                  <div key={message.id} className="flex items-center justify-end gap-2 opacity-50">
                    {String(message.request_data?.author?.id) === String(currentProfile?.id) ? (
                      <button
                        type="button"
                        className="flex size-6 flex-shrink-0 cursor-pointer items-center justify-center text-gray-400 hover:text-gray-600 dark:text-neutral-400 dark:hover:text-neutral-500"
                        onClick={handleRemoveQueuedMessage}
                        data-message-id={message.id}
                        aria-label="Remove queued message"
                      >
                        <X className="size-3.5" />
                      </button>
                    ) : null}
                    <div className="max-w-full min-w-0">
                      <MessageContent message={message} showAuthor={agentChat.kind === 'widget'} />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {showCenteredFooter && <div className="flex-1" />}
            {/* The composer floats over the transcript, so this is what keeps the last
                message clear of it. The studio's composer sits in a deeper gutter, so it
                needs correspondingly more room reserved beneath the messages. */}
            {hasMessages && <div className={compact ? 'h-24' : 'h-8'} />}
            <AgentChatFooter
              agentChat={agentChat}
              centered={showCenteredFooter}
              composerPlaceholder={composerPlaceholder}
              composerAutoSubmit={composerAutoSubmit}
              composerAutoSubmitAttachments={composerAutoSubmitAttachments}
              compact={compact}
              dropzoneHelpers={dropzoneHelpers}
              onDropzoneStateChange={handleDropzoneStateChange}
              submitAgentChatMessage={submitAgentChatMessage}
              onSubmitMessage={handleSubmitMessage}
              onUploadCountChange={handleUploadCountChange}
              maxMediaAttachments={AGENT_CHAT_MESSAGE_MAX_MEDIA_ATTACHMENTS}
              maxRichdownContentSize={AGENT_CHAT_MESSAGE_MAX_RICHDOWN_CONTENT_SIZE}
              composerDisabled={!isContentReady}
              stopTarget={manageableStopTarget}
              onSubmittedRequestChange={setSubmittedRequest}
            />
            {showCenteredFooter && <div className="flex-1" />}
          </>
        )}
      </StickToBottom.Content>
    </PopoverZIndexContext.Provider>
  );

  return (
    <div {...rootProps}>
      <DropOverlay isVisible={showDropzoneOverlay} label="Upload to Agent" zIndexVariant="agentChat" />
      <StickToBottom className="stick-to-bottom agent-chat-scroller flex size-full flex-col" resize="smooth" initial="instant">
        {showHeader ? (
          <AgentChatHeader agentChat={agentChat} onAgentChatChange={onAgentChatChange} onCreateAgentChat={onCreateAgentChat} />
        ) : null}

        <CenteredContentView
          className="flex min-h-0 flex-1 flex-col py-3 sm:py-4 lg:py-0"
          contentClassName="flex min-h-0 flex-1 flex-col px-3 sm:px-4 relative"
        >
          {mainColumn}
        </CenteredContentView>
      </StickToBottom>
    </div>
  );
};

export default AgentChatView;
